import { createContext, useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

import { type ExcalidrawElement, type ExcalidrawImageElement } from '@excalidraw/excalidraw/types/element/types';
import { type BinaryFileData, type BinaryFiles } from '@excalidraw/excalidraw/types/types';
import jwtDecode from 'jwt-decode';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';

import { ADMIN_SETTINGS, GET_ZOOM_USER_PATH } from '../../../config/apiPaths';
import { localStorageKeys } from '../../../localStorageKeys';
import { launchWhiteboard } from '../../../requests/launchWhiteboard';
import { type SchoolSettings } from '../../../types/apiTypes';
import { type UserToken } from '../../../types/userToken';
import HttpClient from '../../../utils/httpClient';
import { getUserUuidHeaders } from '../../../utils/httpUtils';
import { checkPermission } from '../../../utils/permission';
import { getUrls, getUrlParams } from '../../../utils/url';
import { UserRoles } from '../../roster/types';
import {
    type Element,
    type InitialPageData,
    type LibraryGroup,
    type Page,
    type Presenter,
    type Session,
    SessionTypes,
    type StudentInfo,
    type UserInfo
} from '../types';
import { sortElements } from '../util/layer';
import { getFilesFromElements } from '../util/misc';
import { USER_NOT_FOUND, USER_PROPERTIES_MISSING_IN_TOKEN } from '../whiteboard-errors';
import WhiteboardError from '../WhiteboardError';

export type WhiteboardProviderProps = PropsWithChildren<{
    meetingId?: string;
    classUuid: string;
    token: string;
}> ;

export interface WhiteboardContextInterface {
    classUuid: string;
    getSession: (sessionId: string) => Promise<Session | null>;
    getSessions: () => Promise<Session[]>;
    saveSession: (session: Partial<Session>) => Promise<Session | boolean | null>;
    copySession: (session: Session) => Promise<Session | null>;
    deleteSession: (session: string | Session) => Promise<void>;
    getPage: (pageId: string) => Promise<Page | null>;
    getPages: (sessionId: string) => Promise<Page[]>;
    createPages: (pages: Partial<Page>[]) => Promise<Page[] | null>;
    updatePages: (pages: Partial<Page>[]) => Promise<Page | null>;
    copyPage: (page: Page) => Promise<Page | null>;
    deletePages: (pageIds: string[]) => Promise<void>;
    saveElements: (page: Page, elements: ExcalidrawElement[], update: boolean) => void;
    deleteElements: (elementIds: string[]) => void;
    updatePresenter: (connectionId: string | undefined, session: Session, presenter: Presenter | null) => void;
    updatePresenterPosition: (pushMessage: (data) => void, session: Session, presenter: Presenter | null) => void;
    joinWhiteboardChannel: (socketId: string, channels: WebsocketChannels) => Promise<void>;
    leaveWhiteboardChannel: (socketId: string, channels: WebsocketChannels) => Promise<void>;
    getImageUrl: (page: Page, file: BinaryFileData) => Promise<{ key: string; upload_url: string }>;
    uploadImage: (page: Page, file: BinaryFileData) => Promise<any>;
    delegateImage: (page: Page, imageElements: ExcalidrawImageElement[], file: BinaryFileData) => Promise<void>;
    activateImage: (page: Page, imageElements: ExcalidrawImageElement[], file: BinaryFileData) => Promise<void>;
    getElements: (page: Page) => Promise<Element[]>;
    applyElementLock: (
        originalElement: any,
        otherElementAccess?: Session['other_element_access'],
        userInfo?: UserInfo
    ) => any;
    getInitialData: (page: Page, libraryGroups: LibraryGroup[]) => Promise<InitialPageData>;
    openWhiteboardTab: (session: Session, breakoutNames?: string[]) => Promise<void>;
    getMyUserDetails: () => Promise<UserInfo | null>;
    userInfo: UserInfo | undefined;
    flags: WhiteboardFeatureFlags;
}

type WebsocketChannels = {
    session_ids?: string[];
    page_ids?: string[];
    class_id?: string;
};

interface WhiteboardFeatureFlags {
    screenshot: boolean;
}

const defaultFlags: WhiteboardFeatureFlags = {
    screenshot: false
};

const WhiteboardContext = createContext(undefined as unknown as WhiteboardContextInterface);

const { Provider, Consumer } = WhiteboardContext;

/**
 * Ensure token has required properties.
 * @param token
 */
const checkToken = (token: UserToken) => token;

export const evaluateElementLock = (
    element: ExcalidrawElement,
    otherElementAccess?: Session['other_element_access'],
    userInfo?: UserInfo
): boolean => {
    if (element.locked) {
        // If the element is already locked return true.
        return true;
    }
    // Note otherElementAccess refers to peer modification access mode.
    // If set to "assistant", then only users with the role assistant and above can modify the element.
    if (otherElementAccess !== UserRoles.Assistant) {
        // Peer modifications are allowed for all user roles.
        return false;
    }

    // From this point onwards, evaluate peer modification access since otherElementAccess is set to "assistant".

    if (!userInfo?.role) {
        // If no role available, treat as locked.
        return true;
    }
    if (checkPermission(otherElementAccess, userInfo.role)) {
        // User has higher role than assistant, so they can modify the element.
        return false;
    }

    const userStudentId = userInfo.student_id;
    if (userStudentId) {
        // If the user is the owner of an element then they can modify it.
        return Boolean(element.customData?.student?.student_id !== userStudentId);
    }
    
    // Assume the element is locked if we don't have a userStudentId to compare against element student data.
    return true;
};

/**
 * Common API functions in a class scope.
 * @param props
 */
const WhiteboardProvider = (props: WhiteboardProviderProps) => {
    const { t } = useTranslation('common');
    const { meetingId, classUuid, token, children } = props;
    const { api, vanityUrl } = getUrls();
    const httpClient = useMemo(() => new HttpClient({}, { retryAnyError: true }), []);

    const localUserUuid = window.localStorage.getItem(localStorageKeys.userUuid);

    const search = new URLSearchParams(window.location.search);

    const isTemplateEditor = search.get('isTemplateEditor') === 'true';

    const [userInfo, setUserInfo] = useState<UserInfo | undefined>();
    const [flags, setFlags] = useState<WhiteboardFeatureFlags>(defaultFlags);
    const decodedToken: UserToken = useMemo(() => checkToken(jwtDecode(token)), [token]);

    /**
     * Get current user's info from API. The object returned has minimal properties required for whiteboard usage.
     */
    const getMyUserDetails = useCallback(async (): Promise<UserInfo | null> => {
        try {
            const { data } = await httpClient.post(`${api}${GET_ZOOM_USER_PATH}`, {
                user_uuid: decodedToken.user_uuid
            });
            // Note `student-info` works for all users regardless of role.
            const studentInfoResponse = await httpClient.get<StudentInfo>(
                `${api}/v1/whiteboard/student-info?token=${token}&classUuid=${classUuid}`,
                { headers: getUserUuidHeaders(localUserUuid as string) }
            );
            const { firstName, lastName, studentId, studentUuid, role } = studentInfoResponse.data;

            if (!studentUuid) {
                throw new WhiteboardError(`${USER_PROPERTIES_MISSING_IN_TOKEN}: User properties are missing in token.`);
            }
            const first = firstName ? firstName.trim() : '';
            const last = lastName ? lastName.trim() : '';

            return {
                user_uuid: decodedToken.user_uuid,
                student_uuid: studentUuid!,
                student_id: studentId!,
                userName: first === '' ? last : `${first} ${last}`.trim(),
                role: role ?? decodedToken.role ?? UserRoles.Student,
                picUrl: data?.user?.pic_url,
                breakout_name: getUrlParams().breakout_name
            };
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            // User cannot be found/retrieved.
            return null;
        }
    }, [httpClient, api, decodedToken.user_uuid, decodedToken.role, token, classUuid, localUserUuid]);

    /**
     * Immediately get current user info and store as state.
     */
    useEffect(() => {
        const run = async () => {
            if (isTemplateEditor) {
                return;
            }
            const user = await getMyUserDetails();
            if (!user) {
                throw new WhiteboardError(`${USER_NOT_FOUND}: User not found`);
            }

            setUserInfo(user);
        };

        run();
    }, [getMyUserDetails, isTemplateEditor]);

    useEffect(() => {
        const initializeFlags = async () => {
            const response = await httpClient.get<{ data: SchoolSettings }>(`${api}${ADMIN_SETTINGS}`, {
                headers: getUserUuidHeaders(decodedToken.user_uuid)
            });

            const whiteboardFlags = {
                screenshot: response.data.data.whiteboard_screenshot_feature
            };

            setFlags(whiteboardFlags);
        };

        initializeFlags();
    }, [api, decodedToken.user_uuid, httpClient]);

    /**
     * Get whiteboard session by ID from API.
     * @param {string} sessionId
     */
    const getSession = useCallback(
        async (sessionId: string) => {
            const response: any = await httpClient.get(`${api}/v1/whiteboard/session`, {
                params: {
                    session_id: sessionId,
                    token
                }
            });

            return response.data ? response.data : null;
        },
        [api, httpClient, token]
    );

    /**
     * Get all whiteboard sessions within this class.
     */
    const getSessions = useCallback(async () => {
        const response: any = await httpClient.get(`${api}/v1/whiteboard/session`, {
            params: {
                class_id: classUuid,
                token
            }
        });

        return response.data ?? null;
    }, [api, classUuid, httpClient, token]);

    /**
     * Create or update whiteboard session.
     * @param {Partial<Session>} session
     * @return Session|boolean Returns true/false for updating.
     */
    const saveSession = useCallback(
        async (session: Partial<Session>) => {
            // Ensure page access settings are set to Assistant or higher if the session type is individual whiteboards.
            // Students cannot navigate pages in individual sessions.
            const sessionTypeIndividualOrBreakout =
                session.type === SessionTypes.Individual || session.type === SessionTypes.Breakout;
            const pageWriteAccess = sessionTypeIndividualOrBreakout ? UserRoles.Assistant : session.page_write_access;
            const pageLockAccess = sessionTypeIndividualOrBreakout ? UserRoles.Assistant : session.page_lock_access;
            const otherElementAccess = sessionTypeIndividualOrBreakout
                ? UserRoles.Assistant
                : session.other_element_access;

            const url = `${api}/v1/whiteboard/session`;

            const payload = {
                ...session,
                page_write_access: pageWriteAccess,
                page_lock_access: pageLockAccess,
                other_element_access: otherElementAccess
            };

            if (session.session_id) {
                const response = await httpClient.put(url, payload, { params: { token } });

                return response.status === 200;
            }

            const response = await httpClient.post(url, payload, { params: { token } });
            return response.data ? response.data : null;
        },
        [api, httpClient, token]
    );

    /**
     * Shallow copy of session saved to API.
     * @param {Session} session
     * @return Session
     */
    const copySession = useCallback(
        async (session: Session) => saveSession({ ...session, session_id: '', name: `${session.name} ${t('copy')}` }),
        [saveSession, t]
    );

    /**
     * Delete session from any class.
     * @param {string | Session} session
     */
    const deleteSession = useCallback(
        async (session: string | Session) => {
            await httpClient.delete(`${api}/v1/whiteboard/session`, {
                params: { token },
                data: { session_id: typeof session === 'string' ? session : session.session_id }
            });
        },
        [api, token, httpClient]
    );

    /**
     * Decorate whiteboard element with metadata. This is useful for tracking related information. Such as creator or
     * last updated.
     * @param {ExcalidrawElement} element Element to decorate with metadata.
     * @return ExcalidrawElement Decorated element.
     */
    const addMetaData = useCallback(
        (element: ExcalidrawElement): ExcalidrawElement =>
            ({
                ...element,
                metadata: {
                    ...(element as any).metadata,
                    lastUpdatedBy: decodedToken.student_id
                }
            }) as unknown as ExcalidrawElement,
        [decodedToken.student_id]
    );

    /**
     * Get all elements for a page from API.
     * @param {Page} page
     * @return Promise<Element[]>
     */
    const getElements = useCallback(
        async (page: Page): Promise<Element[]> => {
            const response = await httpClient.get(`${api}/v1/whiteboard/element`, {
                params: { page_id: page.page_id, token }
            });
            return response.data;
        },
        [api, token, httpClient]
    );

    /**
     * Create or update elements for a page via API.
     * @param {Page} page Page the elements live in.
     * @param {ExcalidrawElement[]} excalidrawElements Elements to save.
     * @param {boolean} update Set `true` if elements already exist and need updating in the backend.
     */
    const saveElements = useCallback(
        async (page: Page, excalidrawElements: ExcalidrawElement[], update: boolean = false) => {
            const elements = excalidrawElements.filter((element) => {
                if (element.customData?.loading) {
                    // Do not save elements that are still loading.
                    return false;
                }
                if (element.customData?.isLocal) {
                    // Do not save local elements.
                    return false;
                }
                return true;
            });
            // If no elements, do not save.
            if (elements.length === 0) {
                return;
            }
            const url = `${api}/v1/whiteboard/element`;
            const data = {
                page_id: page.page_id,
                elements: elements.map(addMetaData)
            };

            try {
                if (update) {
                    await httpClient.put(url, data, { params: { token } });
                } else {
                    await httpClient.post(url, data, { params: { token } });
                }
            } catch (err) {}
        },
        [addMetaData, api, token, httpClient]
    );

    /**
     * Delete any elements from the backend by ID(s).
     * @param {string[]} elementIds
     */
    const deleteElements = useCallback(
        async (elementIds: string[]) => {
            try {
                await httpClient.delete(`${api}/v1/whiteboard/element`, {
                    params: { token },
                    data: { element_ids: elementIds }
                });
            } catch (err) {}
        },
        [api, token, httpClient]
    );

    /**
     * Get page from API.
     * @param {string} pageId
     */
    const getPage = useCallback(
        async (pageId: string): Promise<Page | null> => {
            const response = await httpClient.get<Page>(`${api}/v1/whiteboard/page`, {
                params: {
                    page_id: pageId,
                    token
                }
            });

            return response.data ? response.data : null;
        },
        [api, token, httpClient]
    );

    /**
     * Get pages for a given whiteboard session from API.
     * @param {string} sessionId
     */
    const getPages = useCallback(
        async (sessionId: string): Promise<Page[]> => {
            const response: any = await httpClient.get(`${api}/v1/whiteboard/page`, {
                params: {
                    session_id: sessionId,
                    token
                }
            });

            return response.data ? response.data : [];
        },
        [api, token, httpClient]
    );

    /**
     * Create pages via API.
     */
    const createPages = useCallback(
        async (pages: Partial<Page>[]): Promise<Page[] | null> => {
            const response = await httpClient.post(`${api}/v1/whiteboard/page`, { pages }, { params: { token } });

            return response.data ? response.data : [];
        },
        [api, token, httpClient]
    );

    /**
     * Update page via API.
     * @param {Partial<Page>} page
     */
    const updatePages = useCallback(
        async (pages: Partial<Page>[]): Promise<Page | null> => {
            const response = await httpClient.put(`${api}/v1/whiteboard/page`, { pages }, { params: { token } });

            return response.data ? response.data : null;
        },
        [api, token, httpClient]
    );

    /**
     * Copy page and all its elements via API.
     * @param {Page} page Page to copy.
     * @return {Page} Copied page.
     */
    const copyPage = useCallback(
        async (page: Page) => {
            const copiedPages =
                (await createPages([{ ...page, page_id: '', name: `${page.name} ${t('copy')}` }])) ?? [];
            const remoteElements = await getElements(page);

            const elements: ExcalidrawElement[] = remoteElements.map((element: Element) => ({
                ...element.body,
                id: nanoid()
            }));
            if (copiedPages && copiedPages[0]) {
                saveElements(copiedPages[0], elements);
                return copiedPages[0];
            }
            return null;
        },
        [getElements, saveElements, createPages, t]
    );

    /**
     * Delete any page.
     * @param {string | Page} page
     */
    const deletePages = useCallback(
        async (pageIds: string[]) => {
            await httpClient.delete(`${api}/v1/whiteboard/page`, {
                params: { token },
                data: { page_ids: pageIds }
            });
        },
        [api, token, httpClient]
    );

    /**
     * Update presenter user info and screen ratios
     */
    const updatePresenter = useCallback(
        async (connectionId: string | undefined, session: Session, presenter: Presenter | null) => {
            let isSame = false;
            if (presenter === session?.data?.presenter) {
                isSame = true;
            }
            let isSameStudentId = false;
            if (presenter?.student_id === session?.data?.presenter?.student_id) {
                isSameStudentId = true;
            }
            let isSameCurrentPageId = false;
            if (presenter?.currentPageId === session?.data?.presenter?.currentPageId) {
                isSameCurrentPageId = true;
            }
            let hasDimensions = Boolean(session?.data?.presenter?.height);
            if (isSame) {
                return;
            }
            if (isSameStudentId && isSameCurrentPageId && hasDimensions) {
                return;
            }
            session.data = {
                ...session.data,
                presenter
            };
            await httpClient.post(
                `${api}/v1/whiteboard/session/presenter`,
                { session_id: session.session_id, presenter, connection_id: connectionId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        },
        [api, httpClient, token]
    );

    /**
     * Update presenter's pointer position (x, y)
     */
    const updatePresenterPosition = useCallback(
        async (pushMessage: (data) => void, session: Session, presenter?: Presenter | null) => {
            /*await httpClient.post(
                `${api}/v1/whiteboard/session/presenter-position`,
                { session_id: session.session_id, position },
                { headers: { Authorization: `Bearer ${token}` } }
            );*/
            await pushMessage({
                channels: [`whiteboard-session:${session.session_id}`],
                type: 'generic',
                body: { func: 'update_presenter_position', data: { presenter } }
            });
        },
        []
    );

    /**
     * "Launch" whiteboard session by opening tabs in all or specific rooms.
     */
    const openWhiteboardTab = useCallback(
        async (session: Session, breakoutNames?: string[]) => {
            if (meetingId === undefined) {
                throw new Error('Cannot launch whiteboard because meetingId is undefined');
            }

            const payload: any = {
                title: session.name,
                tab_type: 'whiteboard',
                control_type: 'manual',
                data: {
                    uniqueId: session.session_id,
                    url: `${vanityUrl}/react/whiteboard/view/${session.session_id}?meeting_id=${meetingId}&class_id=${classUuid}`
                }
            };

            if (breakoutNames) {
                payload.data.data = {};
                payload.data.breakout_names = breakoutNames;
                payload.data.data.breakout_names = breakoutNames;
            }

            await launchWhiteboard(payload, meetingId, token);
        },
        [classUuid, meetingId, token, vanityUrl]
    );

    /**
     * Join or leave websocket channels.
     * @param {'join' | 'leave'} action
     * @param {string} socketId Unique ID given when connecting to websocket server.
     * @param {WebsocketChannels} channels Channels for request.
     */
    const sendWhiteboardChannelRequest = useCallback(
        async (action: 'join' | 'leave', socketId: string, channels: WebsocketChannels) => {
            const response = await httpClient.post(
                `${api}/v1/whiteboard/${action}`,
                {
                    socket_id: socketId,
                    ...channels
                },
                {
                    params: { token }
                }
            );

            return response.data;
        },
        [api, token, httpClient]
    );

    /**
     * Join one or more websocket channels to receive whiteboard session, page, and/or element events.
     * @param {string} socketId Unique ID given when connecting to websocket server.
     * @param {WebsocketChannels} channels Channels to join.
     */
    const joinWhiteboardChannel = useCallback(
        async (socketId: string, channels: WebsocketChannels) =>
            sendWhiteboardChannelRequest('join', socketId, channels),
        [sendWhiteboardChannelRequest]
    );

    /**
     * Stop receiving events in one or more channels.
     * @param {string} socketId Unique ID given when connecting to websocket server.
     * @param channels Channels to leave.
     */
    const leaveWhiteboardChannel = useCallback(
        async (socketId: string, channels: WebsocketChannels) =>
            sendWhiteboardChannelRequest('leave', socketId, channels),
        [sendWhiteboardChannelRequest]
    );

    /**
     * Get signed image upload URL. This is used for adding new Excalidraw image files to Amazon S3.
     * Files added to the canvas are local only until they are uploaded.
     * @param {Page} page
     * @param {BinaryFileData} file
     */
    const getImageUrl = useCallback(
        async (page: Page, file: BinaryFileData) => {
            const extension = file.mimeType.split('/')[1];
            const response = await httpClient.get(`${api}/v1/whiteboard/image-url`, {
                params: {
                    page_id: page.page_id,
                    key: `${file.id}.${extension}`,
                    token
                }
            });

            return response.data;
        },
        [api, token, httpClient]
    );

    /**
     * Upload new Excalidraw image file to Amazon S3
     * @param {Page} page
     * @param {BinaryFileData} file
     */
    const uploadImage = useCallback(
        async (page: Page, file: BinaryFileData) => {
            // Get signed upload url.
            const imageUrlInfo = await getImageUrl(page, file);
            // Load file data from base64.
            const fileData = await fetch(file.dataURL);

            // Construct file information with blob and upload to S3 URL.
            const blob = await fileData.blob();
            const extension = file.mimeType.split('/')[1];
            const finalFile = new File([blob], `${file.id}.${extension}`, { type: file.mimeType });

            await httpClient.put(imageUrlInfo.upload_url, finalFile, {
                headers: {
                    'Content-Type': file.mimeType
                }
            });
        },
        [getImageUrl, httpClient]
    );

    /**
     * Handle new file/image element. Since images take time to upload, the image elements need to be saved outside the
     * normal persistence mechanism. If the user changes page, the image will still be saved to the correct page.
     * @param page
     * @param imageElements
     * @param file
     */
    const delegateImage = useCallback(
        async (page: Page, imageElements: ExcalidrawImageElement[], file: BinaryFileData) => {
            await uploadImage(page, file);
            // Image elements need `key` property that matches the key used when uploading file to s3.
            const extension = file.mimeType.split('/')[1];

            // Save new image elements.
            // Image elements need `key` property that matches the key used when uploading file to s3.
            saveElements(
                page,
                imageElements.map((e) => ({
                    ...e,
                    version: e.version + 1,
                    key: `${file.id}.${extension}`,
                    customData: {
                        ...e.customData,
                        key: `${file.id}.${extension}`,
                        loading: false,
                        loaded: true
                    }
                })),
                false
            );
        },
        [saveElements, uploadImage]
    );

    const activateImage = useCallback(
        async (page: Page, imageElements: ExcalidrawImageElement[], file: BinaryFileData) => {
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate delay for image upload.
            // Image elements need `key` property that matches the key used when uploading file to s3.
            const extension = file.mimeType.split('/')[1];

            // Save new image elements.
            // Image elements need `key` property that matches the key used when uploading file to s3.
            saveElements(
                page,
                imageElements.map((e) => ({
                    ...e,
                    version: e.version + 1,
                    key: `${file.id}.${extension}`,
                    customData: {
                        ...e.customData,
                        key: `${file.id}.${extension}`,
                        loading: false,
                        loaded: true
                    }
                })),
                false
            );
        },
        [saveElements]
    );

    /**
     * Lock an element if "other element access" is only allowed for assitants, current user is a student and the element does not belong to them.
     */
    const applyElementLock = useCallback(
        (
            originalElement: any,
            otherElementAccess?: Session['other_element_access'],
            userInfo?: UserInfo | undefined
        ) => {
            const element = { ...originalElement };

            if (otherElementAccess === UserRoles.Assistant) {
                element.locked = evaluateElementLock(element, otherElementAccess, userInfo);
            }
            return element;
        },
        []
    );

    /**
     * Return all elements and files for a whiteboard page. This is used when loading Excalidraw
     * @param {Page} page
     * @param {LibraryGroup[]} libraryGroups
     */
    const getInitialData = useCallback(
        async (page: Page, libraryGroups: LibraryGroup[] = []): Promise<InitialPageData> => {
            // Get all elements from the backend for initial load.
            const remoteElements = await getElements(page);

            const elements: ExcalidrawElement[] = sortElements(remoteElements.map((element: Element) => element.body));

            // Build file object that Excalidraw can understand from elements (elements decorated with image URL property).
            const filesArray = getFilesFromElements(elements);
            const files: BinaryFiles = Object.fromEntries(filesArray.map((e) => [e.id, e]));

            // Optionally add library element files.
            libraryGroups.forEach((libraryGroup: any) => {
                if (!libraryGroup.files) return;
                // eslint-disable-next-line guard-for-in,no-restricted-syntax
                for (const fileId in libraryGroup.files) {
                    const file = libraryGroup.files[fileId];
                    files[fileId] = file;
                }
            });

            return {
                elements,
                files
            };
        },
        [getElements]
    );

    const value = useMemo(
        () => ({
            classUuid,
            getSession,
            getSessions,
            saveSession,
            copySession,
            deleteSession,
            getPage,
            getPages,
            createPages,
            updatePages,
            copyPage,
            deletePages,
            saveElements,
            deleteElements,
            updatePresenter,
            updatePresenterPosition,
            joinWhiteboardChannel,
            leaveWhiteboardChannel,
            getImageUrl,
            uploadImage,
            delegateImage,
            getElements,
            applyElementLock,
            getInitialData,
            openWhiteboardTab,
            getMyUserDetails,
            userInfo,
            flags,
            activateImage
        }),
        [
            classUuid,
            getSession,
            getSessions,
            saveSession,
            copySession,
            deleteSession,
            getPage,
            getPages,
            createPages,
            updatePages,
            copyPage,
            deletePages,
            saveElements,
            deleteElements,
            updatePresenter,
            updatePresenterPosition,
            joinWhiteboardChannel,
            leaveWhiteboardChannel,
            getImageUrl,
            uploadImage,
            delegateImage,
            getElements,
            applyElementLock,
            getInitialData,
            openWhiteboardTab,
            getMyUserDetails,
            userInfo,
            flags,
            activateImage
        ]
    );

    return <Provider value={value}>{children}</Provider>;
};

export { WhiteboardContext, WhiteboardProvider, Consumer as WhiteboardConsumer };
